// hooks/useTableFetch.ts
import { lazy, useCallback, useEffect, useMemo, useState } from "react";
import { useLocation } from "react-router-dom";
import { useFetch } from "../../../hooks/useFetch";
import { AuthRouteTables, IGameRecord } from "../../../types/types";
import { IBaseFormProps } from "../types/types";
import { fetchMatchGames } from "../services/updateServices";
import { getAdminSectionFromPath } from "../adminNav";
import { STAFF_TABLE_COLUMNS } from "../staffTableDisplay";
import { TEAMS_TABLE_COLUMNS } from "../teamsTableDisplay";
import { CompactTableColumn } from "../compactTableColumn";

const LazyTeamForm = lazy(() =>
  import("../forms/TeamsForm").then((module) => ({ default: module.TeamForm })),
);
const LazyStaffForm = lazy(() =>
  import("../forms/StaffForm").then((module) => ({ default: module.StaffForm })),
);
const LazyMatchForm = lazy(() =>
  import("../forms/MatchesForm").then((module) => ({ default: module.MatchForm })),
);
const LazyPlayerForm = lazy(() =>
  import("../forms/PlayerForm").then((module) => ({ default: module.PlayerForm })),
);

export interface ITableExtendedProps<T> {
  loading: boolean;
  data: T[] | undefined;
  error: string;
  title: string;
  headers: string[];
  keys: Array<keyof T>;
  idKey: keyof T;
  FormComponent: React.ComponentType<IBaseFormProps<T>>;
  gameIdsByMatchId?: Record<string, number[]>;
  gamesByMatchId?: Record<string, IGameRecord[]>;
  compactColumns?: CompactTableColumn<T>[];
  refetchGames?: () => void;
  refetchData?: () => void;
  ensureGamesLoaded?: () => void;
}

const EmptyForm = () => null;

const TABLE_CONFIGS = {
  [AuthRouteTables.TEAMS]: {
    keys: [
      "id",
      "name",
      "type",
      "desciption_en",
      "description_fin",
      "website",
      "twitter",
      "discord",
      "image",
    ],
    headers: [
      "ID",
      "Nimi",
      "Liiga",
      "Kuvaus (EN)",
      "Kuvaus (FI)",
      "Verkkosivu",
      "Twitter",
      "Discord",
      "Kuva",
    ],
    FormComponent: LazyTeamForm,
  },
  [AuthRouteTables.MATCHES]: {
    keys: [
      "id",
      "time",
      "teamOne",
      "teamOneName",
      "teamTwo",
      "teamTwoName",
      "teamOneScore",
      "teamTwoScore",
      "matchType",
      "link",
      "status",
      "bracket",
      "bracketRound",
      "bracketPosition",
    ],
    headers: [
      "ID",
      "Aika",
      "Joukkue 1 ID",
      "Joukkue 1 Nimi",
      "Joukkue 2 ID",
      "Joukkue 2 Nimi",
      "Joukkue 1 Pisteet",
      "Joukkue 2 Pisteet",
      "Ottelutyyppi",
      "Striimilinkki",
      "Tila",
      "Kaavio",
      "Kaaviokierros",
      "Kaaviopaikka",
    ],
    FormComponent: LazyMatchForm,
  },
  [AuthRouteTables.STAFF]: {
    keys: ["name", "link", "handle", "role", "description_en", "description_fin"],
    headers: ["Nimi", "Linkki", "Nimimerkki", "Rooli", "Kuvaus (EN)", "Kuvaus (FI)"],
    FormComponent: LazyStaffForm,
  },
  [AuthRouteTables.PLAYERS]: {
    keys: ["id", "name", "displayName", "position", "playerType", "teamId"],
    headers: ["ID", "Nimi", "Pelinimi", "Pelipaikka", "Pelaajatyyppi", "Joukkue ID"],
    FormComponent: LazyPlayerForm,
  },
} as const;

export const useTableFetch = <T extends object>(): ITableExtendedProps<T> => {
  const pathname = useLocation().pathname;
  const location = pathname.replace("/admin/", "");
  const apiEndpointMap: Record<string, string> = {
    [AuthRouteTables.TEAMS]: "teams/admin",
    [AuthRouteTables.MATCHES]: "matches",
    [AuthRouteTables.STAFF]: "staff",
    [AuthRouteTables.PLAYERS]: "players",
  };
  const apiFetchUrl = apiEndpointMap[location] || location;
  const { loading, data, error, refetch } = useFetch<T[]>(apiFetchUrl, { cache: true });
  const [gameIdsByMatchId, setGameIdsByMatchId] = useState<Record<string, number[]>>({});
  const [gamesByMatchId, setGamesByMatchId] = useState<Record<string, IGameRecord[]>>({});
  const isMatchesTab = location === AuthRouteTables.MATCHES;

  const loadGameIds = useCallback(() => {
    if (!isMatchesTab) return;

    void fetchMatchGames()
      .then((games) => {
        const idsMap: Record<string, number[]> = {};
        const recordsMap: Record<string, IGameRecord[]> = {};
        for (const game of games) {
          const matchKey = String(game.match_id);
          (idsMap[matchKey] ??= []).push(game.game_id);
          (recordsMap[matchKey] ??= []).push(game);
        }
        for (const key of Object.keys(idsMap)) {
          idsMap[key].sort((a, b) => a - b);
        }
        setGameIdsByMatchId(idsMap);
        setGamesByMatchId(recordsMap);
      })
      .catch((err: unknown) => {
        console.error("Failed to load game records:", err);
      });
  }, [isMatchesTab]);

  const tableConfig = useMemo(() => {
    if (!(location in TABLE_CONFIGS)) {
      return {
        headers: [] as string[],
        keys: [] as Array<keyof T>,
        idKey: "id" as keyof T,
        FormComponent: EmptyForm as unknown as React.ComponentType<IBaseFormProps<T>>,
      };
    }

    const config = TABLE_CONFIGS[location as keyof typeof TABLE_CONFIGS];

    return {
      headers: [...config.headers],
      keys: [...config.keys] as Array<keyof T>,
      idKey: "id" as keyof T,
      FormComponent: config.FormComponent as unknown as React.ComponentType<IBaseFormProps<T>>,
    };
  }, [location]);

  useEffect(() => {
    if (!isMatchesTab) {
      setGameIdsByMatchId({});
      setGamesByMatchId({});
      return;
    }
    loadGameIds();
  }, [isMatchesTab, loadGameIds]);

  return {
    loading,
    data,
    error,
    title: getAdminSectionFromPath(pathname)?.text ?? "Hallintapaneeli",
    ...tableConfig,
    gameIdsByMatchId: isMatchesTab ? gameIdsByMatchId : undefined,
    gamesByMatchId: isMatchesTab ? gamesByMatchId : undefined,
    compactColumns:
      location === AuthRouteTables.STAFF
        ? (STAFF_TABLE_COLUMNS as CompactTableColumn<T>[])
        : location === AuthRouteTables.TEAMS
          ? (TEAMS_TABLE_COLUMNS as CompactTableColumn<T>[])
          : undefined,
    refetchGames: isMatchesTab ? loadGameIds : undefined,
    ensureGamesLoaded: isMatchesTab ? loadGameIds : undefined,
    refetchData: refetch,
  };
};
